import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import {
    Box, Grid, Paper, Typography, TextField, InputAdornment,
    Card, CardContent, Button,
    IconButton, List, ListItem, ListItemText,
    Chip, Dialog, DialogTitle, DialogContent, DialogActions,
    CircularProgress, Tab, Tabs
} from '@mui/material';
import ListItemButton from '@mui/material/ListItemButton';
import {
    Search as SearchIcon,
    ShoppingCart as CartIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Remove as RemoveIcon,
    Receipt as ReceiptIcon,
    PointOfSale as PointOfSaleIcon,
    CheckCircle as CheckCircleIcon,
    MonetizationOn as MoneyIcon,
    Print as PrintIcon,
    Assessment as AssessmentIcon,
    History as HistoryIcon
} from '@mui/icons-material';
import axios from 'axios';
import { API_URL } from '../config/api';

// Types
interface Product {
    id: string;
    name: string;
    price: number;
    stockQuantity: number;
    category?: string;
    imageUrl?: string;
    type: 'GOODS' | 'SERVICE';
}

interface CartItem extends Product {
    quantity: number;
}

export interface PosPageHandle {
    triggerCheckout: () => void;
}

const PosPage = forwardRef<PosPageHandle, any>((props, ref) => {
    const [products, setProducts] = useState<Product[]>([]);
    const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
    const [cart, setCart] = useState<CartItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('All');
    const [categories, setCategories] = useState<string[]>(['All']);

    // School Profile State
    const [schoolProfile, setSchoolProfile] = useState<any>(null);

    // Daily Report State
    const [dailyReportOpen, setDailyReportOpen] = useState(false);
    const [dailyStats, setDailyStats] = useState<any>(null);
    const [loadingReport, setLoadingReport] = useState(false);
    const [reportTab, setReportTab] = useState(0);

    const handleReportTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setReportTab(newValue);
    };

    // Checkout State
    const [checkoutOpen, setCheckoutOpen] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState('CASH');
    const [amountTendered, setAmountTendered] = useState('');
    const [processing, setProcessing] = useState(false);
    const [successOrder, setSuccessOrder] = useState<any>(null);

    useImperativeHandle(ref, () => ({
        triggerCheckout: () => {
            if (cart.length > 0) {
                setCheckoutOpen(true);
            } else {
                alert('Cart is empty!');
            }
        }
    }));

    useEffect(() => {
        fetchProducts();
        fetchSchoolProfile();
    }, []);

    const filterProducts = useCallback(() => {
        let result = products;
        if (selectedCategory !== 'All') {
            result = result.filter(p => (p.category || 'Uncategorized') === selectedCategory);
        }
        if (searchTerm) {
            const lower = searchTerm.toLowerCase();
            result = result.filter(p => p.name.toLowerCase().includes(lower));
        }
        setFilteredProducts(result);
    }, [products, selectedCategory, searchTerm]);

    useEffect(() => {
        filterProducts();
    }, [searchTerm, selectedCategory, products, filterProducts]);

    const fetchSchoolProfile = async () => {
        try {
            const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
            const config = {
                headers: {
                    Authorization: `Bearer ${userInfo.token}`
                }
            };
            const response = await axios.get(`${API_URL}/school`, config);
            setSchoolProfile(response.data);
        } catch (error) {
            console.error('Error fetching school profile:', error);
        }
    };

    const fetchProducts = async () => {
        try {
            const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
            const config = {
                headers: {
                    Authorization: `Bearer ${userInfo.token}`
                }
            };
            const response = await axios.get(`${API_URL}/inventory`, config);
            // console.log("Inventory API Response:", response.data); // Debug logging

            // Ensure data is an array
            const data = Array.isArray(response.data) ? response.data : [];
            setProducts(data);

            // Extract categories safely
            const cats = Array.from(new Set(data.map((p: Product) => p.category || 'Uncategorized'))) as string[];
            setCategories(['All', ...cats]);

            setLoading(false);
        } catch (error) {
            console.error('Error fetching products:', error);
            setProducts([]); // Fallback to empty array
            setLoading(false);
        }
    };

    // ... (rest of searchStudents, filterProducts, addToCart, removeFromCart, updateQuantity, cartTotal stays same)

    // Student Search State
    const [studentSearchTerm, setStudentSearchTerm] = useState('');
    const [foundStudents, setFoundStudents] = useState<any[]>([]);
    const [selectedStudent, setSelectedStudent] = useState<any>(null);
    const [searchingStudent, setSearchingStudent] = useState(false);
    const [studentSearchOpen, setStudentSearchOpen] = useState(false);

    const searchStudents = async () => {
        if (!studentSearchTerm) return;
        setSearchingStudent(true);
        try {
            const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
            const config = {
                headers: {
                    Authorization: `Bearer ${userInfo.token}`
                }
            };
            const response = await axios.get(`${API_URL}/pos/students?query=${studentSearchTerm}`, config);
            setFoundStudents(response.data);
        } catch (error) {
            console.error('Error searching students:', error);
        } finally {
            setSearchingStudent(false);
        }
    };


    const addToCart = (product: Product) => {
        setCart(prev => {
            const existing = prev.find(item => item.id === product.id);
            if (existing) {
                return prev.map(item =>
                    item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
                );
            }
            return [...prev, { ...product, quantity: 1 }];
        });
    };


    const updateQuantity = (productId: string, delta: number) => {
        setCart(prev => prev.map(item => {
            if (item.id === productId) {
                const newQty = Math.max(1, item.quantity + delta);
                // Check stock for goods
                if (item.type === 'GOODS' && delta > 0 && newQty > item.stockQuantity) {
                    return item; // Max stock reached
                }
                return { ...item, quantity: newQty };
            }
            return item;
        }));
    };

    const cartTotal = cart.reduce((sum, item) => sum + (Number(item.price) * item.quantity), 0);

    // History State
    const [historyOpen, setHistoryOpen] = useState(false);
    const [historyOrders, setHistoryOrders] = useState<any[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyPage, setHistoryPage] = useState(1);
    const [historyTotalPages, setHistoryTotalPages] = useState(1);
    const [historySearch, setHistorySearch] = useState('');

    const fetchHistory = async (page = 1, query = '') => {
        setHistoryLoading(true);
        try {
            const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
            const config = {
                headers: {
                    Authorization: `Bearer ${userInfo.token}`
                }
            };
            const response = await axios.get(`${API_URL}/pos/history?page=${page}&limit=10&query=${query}`, config);
            setHistoryOrders(response.data.data);
            setHistoryTotalPages(response.data.totalPages);
            setHistoryPage(page);
        } catch (error) {
            console.error('Error fetching history:', error);
        } finally {
            setHistoryLoading(false);
        }
    };

    useEffect(() => {
        if (historyOpen) {
            fetchHistory(historyPage, historySearch);
        }
    }, [historyOpen, historyPage, historySearch]);

    // ... (rest of existing code)

    const handleReprint = (order: any) => {
        // Re-use logic, but we need to ensure 'successOrder' structure matches 'order' structure
        // Or just pass 'order' directly to a print function
        // Let's refactor handlePrintReceipt to accept an order argument
        printReceiptForOrder(order);
    };

    const printReceiptForOrder = (order: any) => {
        const schoolName = schoolProfile?.name || "School POS";
        // ... (rest of profile vars)
        const schoolAddress = schoolProfile?.address || "";
        const schoolPhone = schoolProfile?.phone || "";
        const schoolEmail = schoolProfile?.email || "";
        const logoUrl = schoolProfile?.logo ? `${API_URL.replace('/api/v1', '')}${schoolProfile.logo}` : null;

        const receiptContent = `
            <html>
                <head>
                    <title>Receipt #${order.orderNo}</title>
                   <style>
                        body { font-family: 'Courier New', monospace; padding: 20px; text-align: center; font-size: 14px; }
                        .header { margin-bottom: 20px; border-bottom: 2px dashed #000; padding-bottom: 10px; }
                        .logo { max-width: 80px; max-height: 80px; margin-bottom: 10px; }
                        .school-name { font-size: 18px; font-weight: bold; margin: 5px 0; }
                        .school-info { font-size: 12px; margin: 2px 0; }
                        .meta { margin-top: 10px; font-size: 12px; display: flex; justify-content: space-between; }
                        .items-table { width: 100%; margin-top: 15px; border-collapse: collapse; }
                        .items-table th { text-align: left; border-bottom: 1px solid #000; padding: 5px 0; }
                        .items-table td { text-align: left; padding: 5px 0; }
                        .items-table .price { text-align: right; }
                        .total-section { margin-top: 15px; border-top: 2px dashed #000; padding-top: 10px; }
                        .total-row { display: flex; justify-content: space-between; font-weight: bold; font-size: 16px; margin-top: 5px; }
                        .sub-row { display: flex; justify-content: space-between; font-size: 14px; margin-top: 2px; }
                        .footer { margin-top: 30px; font-size: 12px; border-top: 1px solid #ccc; padding-top: 10px; }
                    </style>
                </head>
                <body>
                    <div class="header">
                         ${logoUrl ? `<img src="${logoUrl}" class="logo" />` : ''}
                        <div class="school-name">${schoolName}</div>
                        ${schoolAddress ? `<div class="school-info">${schoolAddress}</div>` : ''}
                        ${schoolPhone ? `<div class="school-info">Tel: ${schoolPhone}</div>` : ''}
                        ${schoolEmail ? `<div class="school-info">${schoolEmail}</div>` : ''}
                        
                        <div class="meta" style="margin-top: 15px; border-top: 1px solid #eee; padding-top: 5px;">
                            <span>Date: ${new Date(order.createdAt).toLocaleDateString()}</span>
                            <span>Time: ${new Date(order.createdAt).toLocaleTimeString()}</span>
                        </div>
                        <div class="meta">
                            <span>Order: #${order.orderNo}</span>
                            <span>Client: ${order.student ? `${order.student.firstName} ${order.student.lastName}` : 'Walk-in'}</span>
                        </div>
                    </div>

                    <table class="items-table">
                        <thead>
                            <tr>
                                <th>Item</th>
                                <th width="30">Qty</th>
                                <th class="price">Amt</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${order.items.map((item: any) => `
                                <tr>
                                    <td>${item.product?.name || 'Item'}</td>
                                    <td>${item.quantity}</td>
                                    <td class="price">$${(Number(item.unitPrice) * item.quantity).toFixed(2)}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>

                    <div class="total-section">
                        <div class="total-row">
                            <span>TOTAL</span>
                            <span>$${Number(order.totalAmount).toFixed(2)}</span>
                        </div>
                        <div class="sub-row">
                            <span>PAID (${order.paymentMethod.replace('_', ' ')})</span>
                            <span>$${Number(order.totalAmount).toFixed(2)}</span>
                        </div>
                    </div>

                    <div class="footer">
                        <p>Thank you for your business!</p>
                        <p>Please keep this receipt for your records.</p>
                    </div>
                    <script>
                        window.onload = function() { window.print(); }
                    </script>
                </body>
            </html>
        `;

        const printWindow = window.open('', '', 'width=400,height=600');
        if (printWindow) {
            printWindow.document.write(receiptContent);
            printWindow.document.close();
        }
    };

    const handlePrintReceipt = () => {
        if (!successOrder) return;
        printReceiptForOrder(successOrder);
    };

    const fetchDailyStats = async () => {
        setLoadingReport(true);
        try {
            const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
            const config = {
                headers: {
                    Authorization: `Bearer ${userInfo.token}`
                }
            };
            const response = await axios.get(`${API_URL}/pos/daily-report`, config);
            setDailyStats(response.data);
        } catch (error) {
            console.error('Error fetching daily report:', error);
            // Fallback mock data for demo if endpoint fails
            setDailyStats({
                totalSales: 0,
                totalTransactions: 0,
                breakdown: []
            });
        } finally {
            setLoadingReport(false);
        }
    };

    // Auto-fill logic
    useEffect(() => {
        if (checkoutOpen) {
            setAmountTendered(cartTotal.toFixed(2));
        }
    }, [checkoutOpen, cartTotal]);

    const handleCheckout = async () => {
        setProcessing(true);
        try {
            const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
            const config = {
                headers: {
                    Authorization: `Bearer ${userInfo.token}`
                }
            };

            const payload = {
                items: cart.map(item => ({
                    productId: item.id,
                    quantity: item.quantity,
                    unitPrice: item.price
                })),
                paymentMethod,
                amountTendered: Number(amountTendered),
                userId: userInfo.id || userInfo.user?.id,
                studentId: selectedStudent?.id
            };

            const response = await axios.post(`${API_URL}/pos/sales`, payload, config);
            setSuccessOrder(response.data);
            setCart([]);
            fetchProducts(); // Refresh stock
        } catch (error) {
            console.error('Checkout error:', error);
            alert('Error processing sale');
        } finally {
            setProcessing(false);
        }
    };

    const handleCloseCheckout = () => {
        setCheckoutOpen(false);
        setSuccessOrder(null);
        setAmountTendered('');
        setPaymentMethod('CASH');
    };

    // Pastel colors for product backgrounds
    const productColors = ['#e3f2fd', '#e8f5e9', '#fff3e0', '#f3e5f5', '#e0f7fa', '#fce4ec'];
    const getProductColor = (index: number) => productColors[index % productColors.length];

    return (
        <Box sx={{ height: 'calc(100vh - 100px)', display: 'flex', gap: 3, p: 3, bgcolor: '#f0f2f5' }}>
            {/* LEFT: Product Section */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>

                {/* Header & Categories */}
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Typography variant="h4" fontWeight="800" sx={{ color: '#1a237e' }}>
                                Items
                            </Typography>
                            <Button
                                startIcon={<AssessmentIcon />}
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                    setDailyReportOpen(true);
                                    fetchDailyStats();
                                }}
                                sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 'bold' }}
                            >
                                Daily Report
                            </Button>
                            <Button
                                startIcon={<HistoryIcon />}
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                    setHistoryOpen(true);
                                    fetchHistory();
                                }}
                                sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 'bold' }}
                            >
                                History
                            </Button>
                        </Box>
                        <TextField
                            placeholder="Search items..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>,
                                sx: {
                                    borderRadius: '16px',
                                    bgcolor: 'white',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
                                    '& fieldset': { border: 'none' },
                                    width: 300
                                }
                            }}
                            size="small"
                        />
                    </Box>

                    {/* Category Chips */}
                    <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', py: 1, mb: 1, '::-webkit-scrollbar': { display: 'none' } }}>
                        {categories.map((cat) => (
                            <Chip
                                key={cat}
                                label={cat}
                                onClick={() => setSelectedCategory(cat)}
                                sx={{
                                    fontWeight: '700',
                                    borderRadius: '100px',
                                    px: 1.5,
                                    py: 0.5,
                                    fontSize: '0.85rem',
                                    cursor: 'pointer',
                                    bgcolor: selectedCategory === cat ? '#1a237e' : 'white',
                                    color: selectedCategory === cat ? 'white' : '#64748b',
                                    border: selectedCategory === cat ? 'none' : '1px solid #e2e8f0',
                                    boxShadow: selectedCategory === cat ? '0 4px 8px rgba(26, 35, 126, 0.2)' : 'none',
                                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '&:hover': {
                                        bgcolor: selectedCategory === cat ? '#1a237e' : '#f8fafc',
                                        transform: 'translateY(-1px)'
                                    }
                                }}
                            />
                        ))}
                    </Box>
                </Box>

                {/* Product Grid */}
                <Box sx={{ flex: 1, overflowY: 'auto', pr: 1, pb: 2 }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>
                    ) : (
                        <Grid container spacing={2}>
                            {filteredProducts.map((product, index) => (
                                <Grid size={{ xs: 12, sm: 6, md: 4, xl: 3 }} key={product.id}>
                                    <Card
                                        elevation={0}
                                        sx={{
                                            height: '100%',
                                            borderRadius: '24px',
                                            border: '1px solid #f1f5f9',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            cursor: 'pointer',
                                            bgcolor: 'white',
                                            position: 'relative',
                                            overflow: 'hidden',
                                            '&:hover': {
                                                borderColor: '#1a237e',
                                                transform: 'translateY(-4px)',
                                                boxShadow: '0 12px 24px rgba(26, 35, 126, 0.08)',
                                            }
                                        }}
                                        onClick={() => addToCart(product)}
                                    >
                                        <Box sx={{
                                            height: 120,
                                            bgcolor: getProductColor(index),
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            position: 'relative'
                                        }}>
                                            <Typography variant="h2" sx={{ color: 'rgba(0,0,0,0.1)', fontWeight: 900 }}>
                                                {product.name.substring(0, 2).toUpperCase()}
                                            </Typography>
                                            <Box sx={{
                                                position: 'absolute',
                                                top: 12,
                                                right: 12,
                                                bgcolor: 'white',
                                                px: 1.5,
                                                py: 0.5,
                                                borderRadius: '12px',
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                                            }}>
                                                <Typography variant="subtitle2" fontWeight="800" sx={{ color: '#1a237e' }}>
                                                    ${Number(product.price).toFixed(2)}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <CardContent sx={{ flexGrow: 1, p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            <Typography variant="subtitle1" fontWeight="800" sx={{ color: '#1e293b', lineHeight: 1.2, mb: 0.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                                {product.name}
                                            </Typography>

                                            <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                    {product.category || 'General'}
                                                </Typography>
                                                {product.type === 'GOODS' && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: product.stockQuantity > 5 ? '#10b981' : '#f43f5e' }} />
                                                        <Typography variant="caption" sx={{ fontWeight: '700', color: '#64748b' }}>
                                                            {product.stockQuantity}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Box>
            </Box>

            {/* RIGHT: Cart Section */}
            <Paper sx={{
                width: 420,
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '24px',
                bgcolor: 'white',
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                overflow: 'hidden',
                border: '1px solid rgba(0,0,0,0.02)'
            }}>
                {/* Customer / Student Section */}
                <Box sx={{ p: 2, bgcolor: '#e3f2fd', borderBottom: '1px solid #bbdefb' }}>
                    {!selectedStudent ? (
                        <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<SearchIcon />}
                            onClick={() => setStudentSearchOpen(true)}
                            sx={{ bgcolor: 'white', border: '1px dashed #1a237e', color: '#1a237e' }}
                        >
                            Select Student (Optional)
                        </Button>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box>
                                <Typography variant="caption" color="text.secondary" fontWeight="bold">CUSTOMER</Typography>
                                <Typography variant="subtitle2" fontWeight="bold">{selectedStudent.firstName} {selectedStudent.lastName}</Typography>
                                <Typography variant="caption" color={selectedStudent.balance < 0 ? 'error' : 'success'}>
                                    Balance: ${Number(selectedStudent.balance).toFixed(2)}
                                </Typography>
                            </Box>
                            <IconButton size="small" onClick={() => setSelectedStudent(null)} color="error">
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    )}
                </Box>

                {/* Cart Header */}
                <Box sx={{ p: 3, borderBottom: '1px solid #f0f0f0' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="h5" fontWeight="900" sx={{ color: '#1a237e' }}>Current Order</Typography>
                        <IconButton onClick={() => setCart([])} color="error" size="small" disabled={cart.length === 0} sx={{ bgcolor: '#fee2e2', borderRadius: '12px' }}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>
                        ORDER #POS-{Math.floor(Math.random() * 1000)} • {new Date().toLocaleDateString()}
                    </Typography>
                </Box>

                {/* Cart Items */}
                <Box sx={{ flex: 1, overflowY: 'auto', p: 2, bgcolor: '#f8fafc' }}>
                    {cart.length === 0 ? (
                        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', color: '#94a3b8', gap: 2 }}>
                            <Box sx={{ p: 3, bgcolor: '#f1f5f9', borderRadius: '50%' }}>
                                <CartIcon sx={{ fontSize: 40, color: '#cbd5e1' }} />
                            </Box>
                            <Typography fontWeight="700">No items added yet</Typography>
                        </Box>
                    ) : (
                        <List disablePadding>
                            {cart.map(item => (
                                <ListItem key={item.id} sx={{
                                    bgcolor: 'white',
                                    mb: 1.5,
                                    borderRadius: '20px',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.02)',
                                    p: 2,
                                    alignItems: 'center',
                                    border: '1px solid #f1f5f9'
                                }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle2" fontWeight="800" sx={{ mb: 0.5, color: '#1e293b' }}>{item.name}</Typography>
                                        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>
                                            ${Number(item.price).toFixed(2)}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#f1f5f9', borderRadius: '12px', p: 0.5 }}>
                                            <IconButton size="small" onClick={() => updateQuantity(item.id, -1)} disabled={item.quantity <= 1} sx={{ color: '#64748b' }}>
                                                <RemoveIcon fontSize="small" />
                                            </IconButton>
                                            <Typography variant="subtitle2" fontWeight="800" sx={{ px: 1, minWidth: 24, textAlign: 'center', color: '#1e293b' }}>{item.quantity}</Typography>
                                            <IconButton size="small" onClick={() => updateQuantity(item.id, 1)} sx={{ color: '#1a237e' }}>
                                                <AddIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                        <Typography variant="subtitle1" fontWeight="900" sx={{ color: '#1a237e', minWidth: 60, textAlign: 'right' }}>
                                            ${(Number(item.price) * item.quantity).toFixed(2)}
                                        </Typography>
                                    </Box>
                                </ListItem>
                            ))}
                        </List>
                    )}
                </Box>

                {/* Cart Summary & Pay */}
                <Box sx={{ p: 3, bgcolor: 'white', borderTop: '1px solid #f0f0f0' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                        <Typography sx={{ color: '#64748b', fontWeight: 600 }}>Subtotal</Typography>
                        <Typography fontWeight="800" sx={{ color: '#1e293b' }}>${cartTotal.toFixed(2)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                        <Typography sx={{ color: '#64748b', fontWeight: 600 }}>Tax</Typography>
                        <Typography fontWeight="800" sx={{ color: '#1e293b' }}>$0.00</Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, p: 2, bgcolor: '#f8fafc', borderRadius: '16px' }}>
                        <Typography variant="subtitle1" sx={{ color: '#64748b', fontWeight: 700 }}>Total</Typography>
                        <Typography variant="h4" fontWeight="900" sx={{ color: '#1a237e' }}>
                            ${cartTotal.toFixed(2)}
                        </Typography>
                    </Box>

                    <Button
                        variant="contained"
                        fullWidth
                        size="large"
                        onClick={() => setCheckoutOpen(true)}
                        disabled={cart.length === 0}
                        sx={{
                            borderRadius: '16px',
                            py: 2.5,
                            fontWeight: '800',
                            fontSize: '1.1rem',
                            bgcolor: '#1a237e',
                            color: 'white',
                            boxShadow: '0 12px 24px rgba(26, 35, 126, 0.2)',
                            textTransform: 'none',
                            '&:hover': {
                                bgcolor: '#0d1642',
                                boxShadow: '0 16px 32px rgba(26, 35, 126, 0.3)'
                            },
                            '&:disabled': {
                                bgcolor: '#e2e8f0',
                                color: '#94a3b8'
                            }
                        }}
                        startIcon={<PointOfSaleIcon />}
                    >
                        Charge ${cartTotal.toFixed(2)}
                    </Button>
                </Box>
            </Paper>

            {/* Checkout Modal */}
            <Dialog open={checkoutOpen} onClose={() => !processing && setCheckoutOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                    {successOrder ? "Payment Successful" : "Checkout"}
                </DialogTitle>
                <DialogContent>
                    {successOrder ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 2 }}>
                            <CheckCircleIcon color="success" sx={{ fontSize: 80 }} />
                            <Typography variant="h6">Order #{successOrder.orderNo}</Typography>
                            <Typography color="text.secondary">Total Paid: ${Number(successOrder.totalAmount).toFixed(2)}</Typography>
                            <Box sx={{ mt: 2 }}>
                                <Button startIcon={<ReceiptIcon />} variant="outlined" sx={{ mr: 2 }} onClick={handlePrintReceipt}>Print Receipt</Button>
                                <Button variant="contained" onClick={handleCloseCheckout}>New Order</Button>
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h3" fontWeight="bold" color="primary">${cartTotal.toFixed(2)}</Typography>
                                <Typography color="text.secondary">Total Amount Due</Typography>
                            </Box>

                            <Grid container spacing={2}>
                                {['CASH', 'BANK_TRANSFER', 'ECOCASH', 'ONEMONEY', 'INNBUCKS', 'STUDENT_ACCOUNT'].map(method => {
                                    const disabled = method === 'STUDENT_ACCOUNT' && !selectedStudent;
                                    return (
                                        <Grid size={{ xs: 6 }} key={method}>
                                            <Card
                                                sx={{
                                                    cursor: disabled ? 'not-allowed' : 'pointer',
                                                    bgcolor: paymentMethod === method ? '#e3f2fd' : (disabled ? '#f5f5f5' : 'white'),
                                                    border: paymentMethod === method ? '2px solid #2196f3' : '1px solid #e0e0e0',
                                                    borderRadius: '12px',
                                                    opacity: disabled ? 0.6 : 1
                                                }}
                                                onClick={() => {
                                                    if (!disabled) {
                                                        setPaymentMethod(method);
                                                        setAmountTendered(cartTotal.toFixed(2));
                                                    }
                                                }}
                                            >
                                                <CardContent sx={{ textAlign: 'center', py: 2 }}>
                                                    <MoneyIcon color={paymentMethod === method ? "primary" : (disabled ? "disabled" : "action")} />
                                                    <Typography variant="caption" display="block" fontWeight="bold" color={disabled ? "text.disabled" : "text.primary"}>
                                                        {method.replace('_', ' ')}
                                                    </Typography>
                                                    {disabled && <Typography variant="caption" display="block" color="error" fontSize={10}>Select Customer First</Typography>}
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    );
                                })}
                            </Grid>

                            <TextField
                                label="Amount Tendered"
                                type="number"
                                fullWidth
                                value={amountTendered}
                                onChange={(e) => setAmountTendered(e.target.value)}
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">$</InputAdornment>
                                }}
                            />

                            {amountTendered && Number(amountTendered) >= cartTotal && (
                                <Box sx={{ bgcolor: '#e8f5e9', p: 2, borderRadius: '12px', display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography fontWeight="bold" color="success.main">Change Due:</Typography>
                                    <Typography fontWeight="bold" color="success.main">
                                        ${(Number(amountTendered) - cartTotal).toFixed(2)}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                {!successOrder && (
                    <DialogActions sx={{ p: 3 }}>
                        <Button onClick={() => setCheckoutOpen(false)} size="large">Cancel</Button>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={handleCheckout}
                            disabled={processing || (paymentMethod === 'CASH' && Number(amountTendered) < cartTotal)}
                            sx={{ minWidth: 150, borderRadius: '12px' }}
                        >
                            {processing ? <CircularProgress size={24} /> : "Complete Payment"}
                        </Button>
                    </DialogActions>
                )}
            </Dialog>

            {/* Daily Report Dialog */}
            <Dialog
                open={dailyReportOpen}
                onClose={() => setDailyReportOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Daily Sales Report</DialogTitle>
                <DialogContent dividers>
                    {loadingReport ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                            <CircularProgress />
                        </Box>
                    ) : dailyStats ? (
                        <Box>
                            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                                <Tabs value={reportTab} onChange={handleReportTabChange} aria-label="daily report tabs">
                                    <Tab label="Summary" />
                                    <Tab label="Items Sold" />
                                    <Tab label="Transactions" />
                                </Tabs>
                            </Box>

                            {/* Summary Tab */}
                            {reportTab === 0 && (
                                <Grid container spacing={3}>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <Card sx={{ bgcolor: '#e3f2fd', height: '100% ' }}>
                                            <CardContent>
                                                <Typography color="textSecondary" gutterBottom>Total Sales</Typography>
                                                <Typography variant="h4">${Number(dailyStats.totalSales).toFixed(2)}</Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <Card sx={{ bgcolor: '#e8f5e9', height: '100%' }}>
                                            <CardContent>
                                                <Typography color="textSecondary" gutterBottom>Transactions</Typography>
                                                <Typography variant="h4">{dailyStats.totalTransactions}</Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 4 }}>
                                        <Card sx={{ height: '100%' }}>
                                            <CardContent>
                                                <Typography color="textSecondary" gutterBottom>By Payment Method</Typography>
                                                <List dense disablePadding>
                                                    {dailyStats.breakdown.map((item: any, index: number) => (
                                                        <ListItem key={index} disableGutters>
                                                            <ListItemText
                                                                primary={item.method.replace('_', ' ')}
                                                                secondary={`$${Number(item.total).toFixed(2)}`}
                                                            />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            )}

                            {/* Items Sold Tab */}
                            {reportTab === 1 && (
                                <Box>
                                    <Typography variant="subtitle2" gutterBottom>Items Sold Today</Typography>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                                        <thead>
                                            <tr style={{ borderBottom: '2px solid #f0f0f0', textAlign: 'left', color: '#666' }}>
                                                <th style={{ padding: '12px 8px' }}>Product</th>
                                                <th style={{ padding: '12px 8px' }}>Category</th>
                                                <th style={{ padding: '12px 8px' }}>Qty</th>
                                                <th style={{ padding: '12px 8px' }}>Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(() => {
                                                const itemMap = new Map();
                                                dailyStats.orders?.forEach((order: any) => {
                                                    order.items.forEach((item: any) => {
                                                        const key = item.productId;
                                                        if (!itemMap.has(key)) {
                                                            itemMap.set(key, { ...item, totalQty: 0, totalAmount: 0 });
                                                        }
                                                        const entry = itemMap.get(key);
                                                        entry.totalQty += item.quantity;
                                                        entry.totalAmount += Number(item.subtotal);
                                                    });
                                                });
                                                const items = Array.from(itemMap.values());
                                                if (items.length === 0) return <tr><td colSpan={4} style={{ padding: '20px', textAlign: 'center', color: '#999' }}>No items sold yet.</td></tr>;

                                                return items.map((item: any) => (
                                                    <tr key={item.productId} style={{ borderBottom: '1px solid #f5f5f5' }}>
                                                        <td style={{ padding: '12px 8px', fontWeight: 500 }}>{item.product?.name || 'Unknown'}</td>
                                                        <td style={{ padding: '12px 8px', color: '#666' }}>{item.product?.category || '-'}</td>
                                                        <td style={{ padding: '12px 8px' }}>{item.totalQty}</td>
                                                        <td style={{ padding: '12px 8px', fontWeight: 600 }}>${item.totalAmount.toFixed(2)}</td>
                                                    </tr>
                                                ));
                                            })()}
                                        </tbody>
                                    </table>
                                </Box>
                            )}

                            {/* Transactions Tab */}
                            {reportTab === 2 && (
                                <Box>
                                    <Typography variant="subtitle2" gutterBottom>Transactions Today</Typography>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                                        <thead>
                                            <tr style={{ borderBottom: '2px solid #f0f0f0', textAlign: 'left', color: '#666' }}>
                                                <th style={{ padding: '12px 8px' }}>Order #</th>
                                                <th style={{ padding: '12px 8px' }}>Time</th>
                                                <th style={{ padding: '12px 8px' }}>Buyer</th>
                                                <th style={{ padding: '12px 8px' }}>Method</th>
                                                <th style={{ padding: '12px 8px' }}>Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {dailyStats.orders?.length === 0 ? (
                                                <tr><td colSpan={5} style={{ padding: '20px', textAlign: 'center', color: '#999' }}>No transactions yet.</td></tr>
                                            ) : (
                                                dailyStats.orders?.map((order: any) => (
                                                    <tr key={order.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                                                        <td style={{ padding: '12px 8px', fontWeight: 500 }}>{order.orderNo}</td>
                                                        <td style={{ padding: '12px 8px', color: '#666' }}>{new Date(order.createdAt).toLocaleTimeString()}</td>
                                                        <td style={{ padding: '12px 8px' }}>{order.student ? `${order.student.firstName} ${order.student.lastName}` : 'Walk-in'}</td>
                                                        <td style={{ padding: '12px 8px' }}>{order.paymentMethod.replace('_', ' ')}</td>
                                                        <td style={{ padding: '12px 8px', fontWeight: 600 }}>${Number(order.totalAmount).toFixed(2)}</td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </Box>
                            )}
                        </Box>
                    ) : (
                        <Typography align="center" sx={{ py: 3 }}>No data available</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDailyReportOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* History Modal */}
            <Dialog
                open={historyOpen}
                onClose={() => setHistoryOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Sales History</DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
                        <TextField
                            size="small"
                            placeholder="Search Order # or Student"
                            fullWidth
                            value={historySearch}
                            onChange={(e) => setHistorySearch(e.target.value)}
                            onKeyUp={(e) => e.key === 'Enter' && fetchHistory(1, historySearch)}
                        />
                        <Button variant="contained" onClick={() => fetchHistory(1, historySearch)}>Search</Button>
                    </Box>

                    {historyLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>
                    ) : (
                        <>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                                <thead>
                                    <tr style={{ borderBottom: '2px solid #f0f0f0', textAlign: 'left', color: '#666' }}>
                                        <th style={{ padding: '12px 8px' }}>Date</th>
                                        <th style={{ padding: '12px 8px' }}>Order #</th>
                                        <th style={{ padding: '12px 8px' }}>Student</th>
                                        <th style={{ padding: '12px 8px' }}>Amount</th>
                                        <th style={{ padding: '12px 8px' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {historyOrders.length === 0 ? (
                                        <tr><td colSpan={5} style={{ padding: '20px', textAlign: 'center', color: '#999' }}>No history found.</td></tr>
                                    ) : (
                                        historyOrders.map((order: any) => (
                                            <tr key={order.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                                                <td style={{ padding: '12px 8px' }}>{new Date(order.createdAt).toLocaleDateString()} {new Date(order.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                                                <td style={{ padding: '12px 8px', fontWeight: 500 }}>{order.orderNo}</td>
                                                <td style={{ padding: '12px 8px' }}>{order.student ? `${order.student.firstName} ${order.student.lastName}` : 'Walk-in'}</td>
                                                <td style={{ padding: '12px 8px', fontWeight: 600 }}>${Number(order.totalAmount).toFixed(2)}</td>
                                                <td style={{ padding: '12px 8px' }}>
                                                    <Button size="small" startIcon={<PrintIcon />} onClick={() => handleReprint(order)} variant="outlined">
                                                        Print
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, gap: 2, alignItems: 'center' }}>
                                <Button disabled={historyPage === 1} onClick={() => setHistoryPage(p => p - 1)} variant="outlined" size="small">Prev</Button>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>Page {historyPage} of {historyTotalPages || 1}</Typography>
                                <Button disabled={historyPage >= historyTotalPages} onClick={() => setHistoryPage(p => p + 1)} variant="outlined" size="small">Next</Button>
                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setHistoryOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
            {/* Client Search Dialog */}
            <Dialog open={studentSearchOpen} onClose={() => setStudentSearchOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold' }}>Find Student</DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1, display: 'flex', gap: 1 }}>
                        <TextField
                            fullWidth
                            placeholder="Enter Name or ID..."
                            size="small"
                            value={studentSearchTerm}
                            onChange={(e) => setStudentSearchTerm(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), searchStudents())}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '12px'
                                }
                            }}
                        />
                        <Button
                            variant="contained"
                            onClick={searchStudents}
                            disabled={searchingStudent}
                            sx={{ borderRadius: '12px', minWidth: 100 }}
                        >
                            {searchingStudent ? <CircularProgress size={24} color="inherit" /> : 'Search'}
                        </Button>
                    </Box>

                    {foundStudents.length > 0 ? (
                        <List sx={{ mt: 2, bgcolor: '#f5f5f5', borderRadius: '16px', overflow: 'hidden' }}>
                            {foundStudents.map(student => (
                                <ListItem
                                    key={student.id}
                                    disablePadding
                                    divider
                                    secondaryAction={
                                        <Button
                                            size="small"
                                            variant="contained"
                                            onClick={() => {
                                                setSelectedStudent(student);
                                                setFoundStudents([]);
                                                setStudentSearchTerm('');
                                                setStudentSearchOpen(false);
                                            }}
                                            sx={{ borderRadius: '8px' }}
                                        >
                                            Select
                                        </Button>
                                    }
                                >
                                    <ListItemButton>
                                        <ListItemText
                                            primary={`${student.firstName} ${student.lastName}`}
                                            primaryTypographyProps={{ fontWeight: 'bold' }}
                                            secondary={
                                                <Typography variant="caption" display="block">
                                                    ID: {student.studentId} • {student.grade}-{student.class}
                                                </Typography>
                                            }
                                        />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </List>
                    ) : (
                        studentSearchTerm && !searchingStudent && (
                            <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                                <Typography>No students found matching "{studentSearchTerm}"</Typography>
                            </Box>
                        )
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setStudentSearchOpen(false)}>Cancel</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
});

export default PosPage;
